import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import useIsMounted from '../../shared/hooks/useIsMounted';
import { useCurrentUser } from '../auth/CurrentUserContext';
import { fetchWithAuth } from '../auth/fetchWithAuth';
import { ONBOARDING_STEPS_CONFIG } from '../config/onboardingSteps';
import { API_URL } from '../../config/env';

/**
 * useOnboardingProgress
 * 
 * Hook to manage onboarding progress state.
 * Computes which onboarding items are completed based on summary data
 * and user preferences.
 * 
 * Returns:
 *  - steps: Array of step objects with computed completion status
 *  - completedCount: Total number of completed items
 *  - totalCount: Total number of items
 *  - isVisible: Whether onboarding should be shown
 *  - isLoading: Whether data is still loading
 *  - dismissOnboarding: Function to permanently dismiss onboarding
 *  - completeOnboarding: Function to mark onboarding as complete
 *  - markVisited: Function to mark a page as visited
 *  - recordWorkflowCreated: Function to record when user creates a workflow
 */
export default function useOnboardingProgress(summaryData) {
  const isMounted = useIsMounted();
  const { user, updateCurrentUser } = useCurrentUser();
  const [isUpdating, setIsUpdating] = useState(false);
  const completeAttemptedRef = useRef(false);

  // Get preferences from user
  const preferences = user?.preferences || {};
  const onboardingPrefs = useMemo(
    () => preferences.onboarding || {},
    [preferences.onboarding]
  );

  // Determine if onboarding should be visible
  const isFirstUser = user?.first_user ?? false;
  const isCompleted = !!onboardingPrefs.completed_at;
  const isDismissed = !!onboardingPrefs.dismissed_at;
  const isVisible = isFirstUser && !isCompleted && !isDismissed;

  // Extract data from summary
  const {
    totalTasks = 0,
    completedTasks = 0,
    totalAssets = 0,
    totalWorkflows = 0,
    totalUsers = 0,
    latestCompletedTaskAt = null,
  } = summaryData?.onboarding || {};

  // Get the timestamp when user created their workflow (for "complete new workflow" check)
  const lastWorkflowCreatedAt = onboardingPrefs.last_workflow_created_at;

  // Compute individual item completion
  const computeItems = useMemo(() => {
    // Step 1: Basics
    const assignTaskDone = totalTasks >= 1;
    const completeTaskDone = completedTasks >= 1;

    // Step 2: Managing Things
    const addAssetDone = totalAssets >= 2; // 1 auto-created + 1 user-created
    const createWorkflowDone = totalWorkflows >= 2; // 1 default + 1 user-created
    
    // Complete new workflow: a task was completed after the user's workflow was created
    let completeNewWorkflowDone = false;
    if (lastWorkflowCreatedAt && latestCompletedTaskAt) {
      completeNewWorkflowDone = new Date(latestCompletedTaskAt) > new Date(lastWorkflowCreatedAt);
    }
    
    const inviteColleagueDone = totalUsers >= 2;

    // Step 3: Reporting (tracked via preferences)
    const visitedWorkRecord = !!onboardingPrefs.visited_work_record;
    const visitedReports = !!onboardingPrefs.visited_reports;

    return {
      assignTaskDone,
      completeTaskDone,
      addAssetDone,
      createWorkflowDone,
      completeNewWorkflowDone,
      inviteColleagueDone,
      visitedWorkRecord,
      visitedReports,
    };
  }, [
    totalTasks,
    completedTasks,
    totalAssets,
    totalWorkflows,
    totalUsers,
    lastWorkflowCreatedAt,
    latestCompletedTaskAt,
    onboardingPrefs.visited_work_record,
    onboardingPrefs.visited_reports,
  ]);

  // Map config item id -> completion boolean (from computeItems)
  const completionByItemId = useMemo(() => {
    const c = computeItems;
    return {
      assign_task: c.assignTaskDone,
      complete_task: c.completeTaskDone,
      add_asset: c.addAssetDone,
      create_workflow: c.createWorkflowDone,
      complete_new_workflow: c.completeNewWorkflowDone,
      invite_colleague: c.inviteColleagueDone,
      visited_work_record: c.visitedWorkRecord,
      visited_reports: c.visitedReports,
    };
  }, [computeItems]);

  // Build steps from config and merge completion + path for each item
  const steps = useMemo(() => {
    const getStatus = (stepDone, previousStepDone, isFirstStep = false) => {
      if (stepDone) return 'completed';
      if (isFirstStep || previousStepDone) return 'active';
      return 'locked';
    };

    let previousStepDone = true;
    return ONBOARDING_STEPS_CONFIG.map((stepConfig) => {
      const items = stepConfig.items.map((item) => ({
        ...item,
        completed: !!completionByItemId[item.id],
      }));
      const stepDone = items.every((item) => item.completed);
      const status = getStatus(stepDone, previousStepDone, stepConfig.stepNumber === 1);
      previousStepDone = stepDone;

      return {
        ...stepConfig,
        status,
        items,
      };
    });
  }, [completionByItemId]);

  // Calculate totals
  const totalCount = steps.reduce((acc, step) => acc + step.items.length, 0);
  const completedCount = steps.reduce(
    (acc, step) => acc + step.items.filter(item => item.completed).length,
    0
  );

  // Helper to update preferences
  const updatePreferences = useCallback(async (prefsUpdate) => {
    setIsUpdating(true);
    try {
      const res = await fetchWithAuth(
        `${API_URL}/v1/admin/users/me/preferences`,
        {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ preferences: prefsUpdate }),
        }
      );
      if (res.ok) {
        const updatedUser = await res.json();
        updateCurrentUser(updatedUser);
        return true;
      }
      return false;
    } catch (error) {
      if (!isMounted.current) return;
      console.error('Failed to update preferences:', error);
      return false;
    } finally {
      if (!isMounted.current) return;
      setIsUpdating(false);
    }
  }, [updateCurrentUser, isMounted]);

  // Public actions
  const dismissOnboarding = useCallback(() => {
    return updatePreferences({
      onboarding: { dismissed_at: new Date().toISOString() }
    });
  }, [updatePreferences]);

  const completeOnboarding = useCallback(() => {
    return updatePreferences({
      onboarding: { completed_at: new Date().toISOString() }
    });
  }, [updatePreferences]);

  const markVisited = useCallback((page) => {
    const key = page === 'work_record' ? 'visited_work_record' : 'visited_reports';
    // Only update if not already visited
    if (!onboardingPrefs[key]) {
      return updatePreferences({
        onboarding: { [key]: true }
      });
    }
    return Promise.resolve(true);
  }, [onboardingPrefs, updatePreferences]);

  const recordWorkflowCreated = useCallback(() => {
    // Only record if not already recorded
    if (!onboardingPrefs.last_workflow_created_at) {
      return updatePreferences({
        onboarding: { last_workflow_created_at: new Date().toISOString() }
      });
    }
    return Promise.resolve(true);
  }, [onboardingPrefs.last_workflow_created_at, updatePreferences]);

  // Auto-complete onboarding when all steps are done (once per session)
  useEffect(() => {
    const allDone = completedCount === totalCount && totalCount > 0;
    if (allDone && isVisible && !isUpdating && !completeAttemptedRef.current) {
      completeAttemptedRef.current = true;
      completeOnboarding();
    }
  }, [completedCount, totalCount, isVisible, isUpdating, completeOnboarding]);

  return {
    steps,
    completedCount,
    totalCount,
    isVisible,
    isLoading: !user,
    isUpdating,
    dismissOnboarding,
    completeOnboarding,
    markVisited,
    recordWorkflowCreated,
  };
}
